import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { saveAs } from 'file-saver';
import { MotionMasterMessage } from 'motion-master-client';
import { defer, of } from 'rxjs';
import { catchError, filter, map, mergeMap, tap } from 'rxjs/operators';

import {
  deleteDeviceFile,
  deleteDeviceFileRequest,
  deleteDeviceFileStatus,
  getCoggingTorqueDataRequest,
  getDeviceFileRequest,
  getDeviceFileStatus,
  getDeviceLogRequest,
  setDeviceFileRequest,
  setDeviceFileStatus,
  unlockDeviceFileWriteProtectionRequest,
  unlockDeviceFileWriteProtectionStatus,
  upsertDeviceFile,
} from './device-file.actions';
import { deviceFileId, stackUnlockFilename } from './device-file.model';

import {
  deleteDeviceFileCompleted,
  DeviceFileStatus,
  getDeviceFileCompleted,
  IDeviceFile,
} from '@app/models/status-resolve';
import { ConnectionService } from '@app/services/connection.service';
import { DeviceService } from '@app/services/device.service';
import { GlobalService } from '@app/services/global.service';

@Injectable()
export class DeviceFileEffects {
  private actions$ = inject(Actions);
  private cn = inject(ConnectionService);
  private deviceService = inject(DeviceService);
  private globalService = inject(GlobalService);

  //
  // Delete Device File.
  //

  toastrOnDeleteDeviceFileRequest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteDeviceFileRequest),
        tap((action) => {
          const { device, filename, suppressToastr } = action;
          const deviceName = this.deviceService.getDeviceName(device);

          const deviceFile: DeviceFileStatus = {
            begin: {
              message: `Deleting the ${filename} file from ${deviceName}.`,
            },
          };

          this.globalService.toastrStatusMessage(deviceFile, 'Delete Device File', suppressToastr);
        }),
      ),
    { dispatch: false },
  );

  handleDeleteDeviceFileRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteDeviceFileRequest),
      mergeMap((action) => {
        const { device, filename } = action;
        return this.cn.client.request
          .deleteDeviceFile({ deviceAddress: device.deviceAddress, name: filename }, action.timeout)
          .pipe(
            map((deviceFile) => {
              return deleteDeviceFileStatus({
                ...action,
                deviceFile,
              });
            }),
            catchError(() => {
              const deviceName = this.deviceService.getDeviceName(device);

              return of(
                deleteDeviceFileStatus({
                  ...action,
                  deviceFile: {
                    name: filename,
                    timeout: {
                      message: `Timeout while deleting the ${filename} file on ${deviceName}.`,
                    },
                  },
                }),
              );
            }),
          );
      }),
    ),
  );

  handleDeleteDeviceFileStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteDeviceFileStatus),
      tap((action) =>
        this.globalService.toastrStatusMessage(action.deviceFile, 'Delete Device File', action.suppressToastr),
      ),
      filter((action) => deleteDeviceFileCompleted(action.device) && action.deleteFromStore),
      map((action) =>
        deleteDeviceFile({
          id: deviceFileId(action.device, action.deviceFile.name),
        }),
      ),
    ),
  );

  //
  // Get Device File.
  //

  toastrOnGetDeviceFileRequest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getDeviceFileRequest, getDeviceLogRequest, getCoggingTorqueDataRequest),
        tap((action) => {
          const { device, filename, suppressToastr } = action;
          const deviceName = this.deviceService.getDeviceName(device);

          const deviceFile: DeviceFileStatus = {
            begin: {
              message: `Getting the ${filename} file from ${deviceName}.`,
            },
          };

          this.globalService.toastrStatusMessage(deviceFile, 'Get Device File', suppressToastr);
        }),
      ),
    { dispatch: false },
  );

  handleGetDeviceFileRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDeviceFileRequest),
      mergeMap((action) => {
        const { device, filename, decodeText } = action;
        return this.cn.client.request
          .getDeviceFile({ deviceAddress: device.deviceAddress, name: filename }, action.timeout)
          .pipe(
            map((status) => {
              let content: Uint8Array | string | null = status.content ? Uint8Array.from(status.content) : null;
              if (content && decodeText) {
                content = new TextDecoder().decode(content);
              }
              const deviceFile: IDeviceFile = { ...status, content };
              return getDeviceFileStatus({ ...action, deviceFile });
            }),
            catchError(() => {
              const deviceName = this.deviceService.getDeviceName(device);

              return of(
                getDeviceFileStatus({
                  ...action,
                  deviceFile: {
                    name: filename,
                    timeout: {
                      message: `Timeout while getting the ${filename} file from ${deviceName}.`,
                    },
                  },
                }),
              );
            }),
          );
      }),
    ),
  );

  handleGetDeviceFileStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDeviceFileStatus),
      tap((action) => {
        const { device, deviceFile, suppressToastr } = action;
        const deviceName = this.deviceService.getDeviceName(device);

        if (deviceFile.error?.code === MotionMasterMessage.Status.DeviceFile.Error.Code.NOT_FOUND) {
          deviceFile.error.message = `File ${deviceFile.name} is not found on ${deviceName}.`;
          if (suppressToastr) {
            console.warn(deviceFile.error.message);
          }
        }

        this.globalService.toastrStatusMessage(deviceFile, 'Get Device File', suppressToastr);
      }),
      filter((action) => getDeviceFileCompleted(action.deviceFile)),
      filter((action) => {
        if (action.deviceFile.name === 'ui.config.json') {
          try {
            const parsedFile = JSON.parse(action.deviceFile.content);
            if (Object.keys(parsedFile).length === 0) {
              throw new Error('Empty ui.config.json file');
            }
            return true;
          } catch (error) {
            return false;
          }
        }
        return true;
      }),
      tap((action) => {
        if (action.saveToClient) {
          const { device, deviceFile, saveToClientFilename } = action;
          const { name } = deviceFile;
          // Type is set to 'application/x-binary' so that browsers or file-saver (not sure)
          // don't add ".txt" at the end of a file name when no type is set.
          // This seems to work well for both binary and textual files. Tested in Google Chrome and Firefox.
          const blob = new Blob([deviceFile.content], { type: 'application/x-binary' });
          const saveAsFilename = saveToClientFilename ?? this.deviceService.prefixDeviceFilename(device, name);
          saveAs(blob, saveAsFilename);
        }
      }),
      map((action) => {
        const { device, deviceFile } = action;
        return upsertDeviceFile({
          deviceFile: {
            content: deviceFile.content,
            deviceAddress: device.deviceAddress,
            deviceSerialNumber: device.serialNumber,
            id: deviceFileId(device, deviceFile.name),
            name: deviceFile.name,
          },
        });
      }),
    ),
  );

  handleGetCoggingTorqueDataRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCoggingTorqueDataRequest),
      mergeMap((action) => {
        const { device, filename } = action;

        return this.cn.client.request
          .getCoggingTorqueData({ deviceAddress: device.deviceAddress }, action.timeout)
          .pipe(
            map((coggingTorqueData) => {
              const content = coggingTorqueData.table?.data;
              return getDeviceFileStatus({
                ...action,
                deviceFile: {
                  content,
                  deviceAddress: device.deviceAddress,
                  error: coggingTorqueData.error as any,
                  name: filename,
                },
              });
            }),
            catchError(() => {
              const deviceName = this.deviceService.getDeviceName(device);

              return of(
                getDeviceFileStatus({
                  ...action,
                  deviceFile: {
                    name: filename,
                    timeout: {
                      message: `Timeout while getting the ${filename} file from ${deviceName}.`,
                    },
                  },
                }),
              );
            }),
          );
      }),
    ),
  );

  handleGetDeviceLogRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDeviceLogRequest),
      mergeMap((action) => {
        const { device, filename } = action;

        return this.cn.client.request.getDeviceLog({ deviceAddress: device.deviceAddress }, action.timeout).pipe(
          map((deviceLog) => {
            const content = deviceLog.content;
            return getDeviceFileStatus({
              ...action,
              deviceFile: {
                content,
                deviceAddress: device.deviceAddress,
                error: deviceLog.error as any,
                name: filename,
              },
            });
          }),
          catchError(() => {
            const deviceName = this.deviceService.getDeviceName(device);

            return of(
              getDeviceFileStatus({
                ...action,
                deviceFile: {
                  name: filename,
                  timeout: {
                    message: `Timeout while getting the ${filename} file from ${deviceName}.`,
                  },
                },
              }),
            );
          }),
        );
      }),
    ),
  );

  //
  // Set Device File.
  //

  toastrOnSetDeviceFileRequest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setDeviceFileRequest),
        tap((action) => {
          const { device, filename, suppressToastr } = action;
          const deviceName = this.deviceService.getDeviceName(device);

          const deviceFile: DeviceFileStatus = {
            begin: {
              message: `Uploading the ${filename} file to ${deviceName}.`,
            },
          };

          this.globalService.toastrStatusMessage(deviceFile, 'Upload Device File', suppressToastr);
        }),
      ),
    { dispatch: false },
  );

  handleSetDeviceFileRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setDeviceFileRequest),
      mergeMap((action) => {
        const { device, filename, content, unlock } = action;

        const obs = defer(() =>
          this.cn.client.request
            .setDeviceFile(
              { deviceAddress: device.deviceAddress, name: filename, content, overwrite: true },
              action.timeout,
            )
            .pipe(
              map((deviceFile) => {
                return setDeviceFileStatus({
                  ...action,
                  deviceFile,
                });
              }),
              catchError(() => {
                const deviceName = this.deviceService.getDeviceName(device);

                return of(
                  setDeviceFileStatus({
                    ...action,
                    deviceFile: {
                      name: filename,
                      timeout: {
                        message: `Timeout while uploading the ${filename} file to ${deviceName}.`,
                      },
                    },
                  }),
                );
              }),
            ),
        );

        if (unlock) {
          return this.cn.client.request
            .getDeviceFile({ deviceAddress: device.deviceAddress, name: stackUnlockFilename }, action.timeout)
            .pipe(mergeMap(() => obs));
        } else {
          return obs;
        }
      }),
    ),
  );

  handleSetDeviceFileStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setDeviceFileStatus),
        tap((action) =>
          this.globalService.toastrStatusMessage(action.deviceFile, 'Upload Device File', action.suppressToastr),
        ),
      ),
    { dispatch: false },
  );

  // Optimistic device file update.
  upsertDeviceFileOnSetDeviceFileRequest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setDeviceFileRequest),
        map(({ device, filename, content }) => {
          const id = deviceFileId(device, filename);
          return upsertDeviceFile({
            deviceFile: {
              deviceSerialNumber: device.serialNumber,
              id,
              content: new TextDecoder().decode(content),
              deviceAddress: device.deviceAddress,
              name: filename,
            },
          });
        }),
      ),
    { dispatch: true },
  );

  // Pessimistic device file update.
  // getDeviceFileOnSetDeviceFileStatus$ = createEffect(() => this.actions$.pipe(
  //   ofType(setDeviceFileStatus),
  //   map(({ device, deviceFile }) => getDeviceFileRequest({
  //     device,
  //     filename: deviceFile.name,
  //     timeout: 5000,
  //     suppressToastr: true,
  //   })),
  // ), { dispatch: true });

  handleUnlockDeviceFileWriteProtectionRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(unlockDeviceFileWriteProtectionRequest),
      mergeMap((action) => {
        const { device } = action;

        return this.cn.client.request
          .getDeviceFile({ deviceAddress: device.deviceAddress, name: stackUnlockFilename }, action.timeout)
          .pipe(
            map((status) => {
              let content: Uint8Array | string | null = status.content ? Uint8Array.from(status.content) : null;
              content = new TextDecoder().decode(content);
              if (!content.includes('unlocked')) {
                throw new Error(content);
              }
              return unlockDeviceFileWriteProtectionStatus({
                ...action,
                status: { success: { message: 'Protected files are unlocked' } },
                unlocked: true,
              });
            }),
            catchError((error) => {
              return of(
                unlockDeviceFileWriteProtectionStatus({
                  ...action,
                  status: { error: { message: error.message } },
                  unlocked: false,
                }),
              );
            }),
          );
      }),
    ),
  );

  handleUnlockDeviceFileWriteProtectionStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(unlockDeviceFileWriteProtectionStatus),
        tap((action) => {
          const { status, suppressToastr } = action;

          this.globalService.toastrStatusMessage(status, 'Unlock Device File Write Protection', suppressToastr);
        }),
      ),
    { dispatch: false },
  );
}
